import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs, type BankStatement, type HourBankRow, type HourMovement } from '../api';
import { Dialog, Empty, ErrorAlert, Field, fieldError, HowTo, Loading } from '../components/ui';
import { date, hm, money, parseHoursInput, parseMoneyInput, todayIso } from '../format';
import { StatementLinks } from './Relatorios';
import { RelatedMessages } from './Mensagens';

const KIND_LABEL: Record<HourMovement['kind'], string> = {
  credito_inicial: 'Crédito inicial',
  saldo_abertura: 'Saldo de abertura',
  credito_adicional: 'Crédito adicional',
  consumo: 'Consumo',
  estorno: 'Estorno',
  ajuste: 'Ajuste',
};

export function BalanceBadge({ minutes, low }: { minutes: number; low?: boolean }) {
  const cls = minutes <= 0 ? 'badge-demo' : low ? 'badge-demo' : 'badge-ok';
  return <span className={`badge ${cls}`}>Saldo {hm(minutes)}</span>;
}

// ------------------------------------------------------------------ lista

export function HourBanksPage() {
  const [scope, setScope] = useState<'all' | 'mine'>('all');
  const q = useQuery({ queryKey: ['hour-banks', scope], queryFn: () => api.get<{ data: HourBankRow[] }>(`/hour-banks${qs({ scope })}`) });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Bancos de horas</h1>
          <p>Contratado, consumido e saldo de cada banco. O saldo é sempre a soma dos movimentos registrados.</p>
        </div>
      </div>
      <HowTo id="bancos" title="Como funciona o banco de horas">
        <ul>
          <li><strong>Saldo</strong> = horas compradas − horas usadas. Clique no banco para ver o extrato, movimento a movimento.</li>
          <li>As horas só saem do saldo quando você <strong>confirma</strong> um atendimento. Rascunho não consome nada.</li>
          <li><strong>Passou do saldo?</strong> A diferença vira <strong>excedente</strong> e nada é cobrado sozinho. Abra o atendimento e escolha: deduzir de outro banco, faturar avulso ou não cobrar.</li>
          <li><strong>Cliente comprou mais horas?</strong> Abra o banco › <strong>Adicionar horas</strong>. Gera um único item no Faturamento.</li>
          <li><strong>Lançou errado?</strong> Anule o atendimento: as horas voltam como estorno.</li>
        </ul>
      </HowTo>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Escopo">
          <button aria-pressed={scope === 'all'} onClick={() => setScope('all')}>
            Toda a TECHNOVE
          </button>
          <button aria-pressed={scope === 'mine'} onClick={() => setScope('mine')}>
            Meus projetos
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : q.data?.data.length === 0 ? (
        <Empty>Nenhum contrato de banco de horas ativo.</Empty>
      ) : (
        <div className="list">
          {q.data?.data.map((b) => (
            <Link key={b.id} to={`/bancos-de-horas/${b.id}`} className="list-item">
              <div className="title">
                {b.clientName} · {b.projectName} <span className="badge">{b.code}</span>{' '}
                {b.hasInitialCredit ? <BalanceBadge minutes={b.balance} low={b.lowBalance} /> : <span className="badge badge-demo">crédito inicial não registrado</span>}
                {b.lowBalance && b.balance > 0 && <span className="badge badge-demo">saldo baixo</span>}
                {b.pendingOverageCount > 0 && <span className="badge badge-demo">excedente pendente {hm(b.pendingOverageMinutes)}</span>}
              </div>
              <div className="meta">
                <span>Contratado {hm(b.contractedMinutes)}</span>
                <span>Créditos {hm(b.credits)}</span>
                <span>Consumido {hm(b.consumed)}</span>
                <span>Responsável: {b.ownerName}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ extrato

export function HourBankPage() {
  const { contractId } = useParams();
  const [dialog, setDialog] = useState<'inicial' | 'abertura' | 'adicional' | 'ajuste' | null>(null);
  const [editing, setEditing] = useState<HourMovement | null>(null);
  const q = useQuery({ queryKey: ['statement', contractId], queryFn: () => api.get<BankStatement>(`/contracts/${contractId}/statement`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const { contract: c, summary: s, movements, pendingOverages } = q.data!;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/bancos-de-horas">← Bancos de horas</Link> · <Link to={`/projetos/${c.projectId}`}>{c.projectName}</Link>
          </p>
          <h1>
            Banco {c.code} <BalanceBadge minutes={s.balance} low={c.lowBalanceMinutes != null && s.balance <= c.lowBalanceMinutes} />
          </h1>
          <p>
            {c.clientName} · valor da hora {money(c.hourlyRate)}
          </p>
        </div>
        <div className="actions">
          {s.hasInitialCredit && (
            <button className="btn btn-primary" onClick={() => setDialog('adicional')}>
              Adicionar horas
            </button>
          )}
          {s.hasInitialCredit && (
            <button className="btn" onClick={() => setDialog('ajuste')}>
              Ajuste identificado
            </button>
          )}
        </div>
      </div>

      {!s.hasInitialCredit && (
        <div className="alert alert-warning">
          <strong>Crédito inicial ainda não registrado</strong>
          Registre a contratação ({hm(c.contractedMinutes)}) — informando se já foi faturada fora do aplicativo — ou importe o saldo remanescente na data de corte.
          <div className="actions" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-sm btn-primary" onClick={() => setDialog('inicial')}>
              Registrar contratação
            </button>
            <button className="btn btn-sm" onClick={() => setDialog('abertura')}>
              Importar saldo de abertura
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-4 grid-stats" style={{ marginBottom: '1rem' }}>
        <div className="stat">
          <div className="label">Contratado</div>
          <div className="value">{hm(c.contractedMinutes)}</div>
        </div>
        <div className="stat">
          <div className="label">Créditos</div>
          <div className="value">{hm(s.credits)}</div>
          <div className="small muted">
            {s.additionalCredits ? `inclui ${hm(s.additionalCredits)} adicionais` : s.openingBalance ? 'saldo de abertura importado' : ' '}
          </div>
        </div>
        <div className="stat">
          <div className="label">Consumido</div>
          <div className="value">{hm(s.consumed - s.reversed)}</div>
          {s.reversed > 0 && <div className="small muted">{hm(s.reversed)} estornados</div>}
        </div>
        <div className="stat">
          <div className="label">Saldo</div>
          <div className="value">{hm(s.balance)}</div>
          {s.adjustments !== 0 && <div className="small muted">ajustes {hm(s.adjustments)}</div>}
        </div>
      </div>

      {pendingOverages.length > 0 && (
        <section className="card" style={{ borderColor: '#f3d48a' }}>
          <h2>Excedentes aguardando decisão</h2>
          <p className="small muted">Horas executadas além do saldo. Nada é cobrado automaticamente: decida em cada execução.</p>
          <div className="list">
            {pendingOverages.map((o) => (
              <Link key={o.id} className="list-item" to={`/execucoes/${o.id}`}>
                <div className="title">
                  {date(o.serviceDate)} · {hm(o.overageMinutes)} além do saldo
                </div>
                <div className="meta">Executor: {o.executorName}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="card" style={{ padding: 0 }}>
        <div style={{ padding: '1rem 1.25rem 0' }}>
          <h2>Extrato</h2>
        </div>
        {movements.length === 0 ? (
          <div style={{ padding: '0 1.25rem 1rem' }}>
            <Empty>Nenhum movimento.</Empty>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Movimento</th>
                  <th>Origem / referência</th>
                  <th style={{ textAlign: 'right' }}>Horas</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m.id}>
                    <td>{date(m.occurredOn)}</td>
                    <td>{KIND_LABEL[m.kind]}</td>
                    <td className="small">
                      {m.executionId ? (
                        <Link to={`/execucoes/${m.executionId}`}>
                          Execução {date(m.executionDate)} · {m.executorName}
                        </Link>
                      ) : null}
                      {m.reference && <div>Ref.: {m.reference}{m.alreadyInvoiced ? ' (já faturado)' : ''}</div>}
                      {m.kind === 'credito_adicional' && m.hourlyRate && <div>a {money(m.hourlyRate)}/h</div>}
                      {m.reason && <div className="muted">{m.reason}</div>}
                    </td>
                    <td style={{ textAlign: 'right', color: m.minutes < 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                      {m.minutes > 0 ? '+' : '−'}
                      {hm(Math.abs(m.minutes))}
                    </td>
                    <td style={{ textAlign: 'right' }}>{hm(m.balanceAfter)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {m.executionId && (m.kind === 'consumo' || m.kind === 'estorno') ? (
                        <Link className="btn btn-sm btn-ghost" to={`/execucoes/${m.executionId}`} title="Consumos seguem a execução: corrija data ou duração na execução">
                          Editar
                        </Link>
                      ) : (
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(m)}>
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <StatementExport contractId={c.id} />

      {dialog && dialog !== 'ajuste' && <CreditDialog kind={dialog} statement={q.data!} onClose={() => setDialog(null)} />}
      {dialog === 'ajuste' && <AdjustmentDialog contractId={c.id} onClose={() => setDialog(null)} />}
      {editing && <MovementEditDialog contractId={c.id} movement={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

// Editar item do extrato: corrige o lançamento (data, horas, referência, descrição do ajuste).
// O extrato do cliente mostra só o resultado corrigido; o motivo da correção fica apenas na auditoria interna.
function MovementEditDialog({ contractId, movement: m, onClose }: { contractId: string; movement: HourMovement; onClose: () => void }) {
  const qc = useQueryClient();
  const hoursEditable = m.kind === 'credito_adicional' || m.kind === 'saldo_abertura' || m.kind === 'ajuste';
  const isCredit = m.kind === 'credito_inicial' || m.kind === 'credito_adicional' || m.kind === 'saldo_abertura';
  const toInput = (min: number) => {
    const h = Math.floor(Math.abs(min) / 60);
    const r = Math.abs(min) % 60;
    return r ? `${h}h${String(r).padStart(2, '0')}` : String(h);
  };
  const [form, setForm] = useState({
    occurredOn: m.occurredOn,
    hours: toInput(m.minutes),
    sign: (m.minutes < 0 ? -1 : 1) as 1 | -1,
    reference: m.reference ?? '',
    description: m.reason ?? '',
    reason: '',
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const parsed = parseHoursInput(form.hours);
  const mut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { occurredOn: form.occurredOn, reason: form.reason };
      if (hoursEditable) {
        if (!parsed) throw new Error('Informe as horas (ex.: 8 ou 1h30).');
        body.minutes = m.kind === 'ajuste' ? form.sign * parsed : parsed;
      }
      if (isCredit) body.reference = form.reference;
      if (m.kind === 'ajuste') body.description = form.description;
      return api.patch(`/contracts/${contractId}/movements/${m.id}`, body);
    },
    onSuccess: () => {
      for (const key of [['statement', contractId], ['hour-banks'], ['billing-items']]) void qc.invalidateQueries({ queryKey: key });
      onClose();
    },
  });
  return (
    <Dialog title={`Editar: ${KIND_LABEL[m.kind]}`} onClose={onClose}>
      <div className="form">
        <div className="alert alert-info small">
          A correção substitui o lançamento. O extrato do cliente mostra só o resultado corrigido; o motivo fica registrado apenas na auditoria interna.
        </div>
        <ErrorAlert error={mut.error} />
        <Field label={m.kind === 'saldo_abertura' ? 'Data de corte' : m.kind === 'credito_inicial' ? 'Data da contratação' : 'Data'} required>
          {(p) => <input {...p} type="date" value={form.occurredOn} onChange={(e) => set('occurredOn', e.target.value)} />}
        </Field>
        {m.kind === 'ajuste' && (
          <div className="segmented" role="group" aria-label="Tipo de ajuste">
            <button type="button" aria-pressed={form.sign === 1} onClick={() => set('sign', 1)}>
              Acrescentar
            </button>
            <button type="button" aria-pressed={form.sign === -1} onClick={() => set('sign', -1)}>
              Retirar
            </button>
          </div>
        )}
        {hoursEditable ? (
          <Field label="Horas" required hint="Ex.: 8, 1,5 ou 1h30">
            {(p) => <input {...p} inputMode="decimal" value={form.hours} onChange={(e) => set('hours', e.target.value)} />}
          </Field>
        ) : (
          m.kind === 'credito_inicial' && <p className="small muted">Horas: {hm(m.minutes)} (vêm do contrato; para mudar, edite o contrato e use "Adicionar horas" ou um ajuste).</p>
        )}
        {isCredit && (
          <Field label="Documento / referência do faturamento" required={Boolean(m.alreadyInvoiced)}>
            {(p) => <input {...p} value={form.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Ex.: NF 1234" />}
          </Field>
        )}
        {m.kind === 'ajuste' && (
          <Field label="Descrição do ajuste (aparece no extrato)" required>
            {(p) => <textarea {...p} value={form.description} onChange={(e) => set('description', e.target.value)} />}
          </Field>
        )}
        <Field label="Motivo da correção (interno, não aparece no extrato)" required>
          {(p) => <textarea {...p} value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Ex.: lançado com a data do registro em vez da data da contratação" />}
        </Field>
        <div className="actions">
          <button className="btn btn-primary" disabled={form.reason.trim().length < 5 || (hoursEditable && !parsed) || mut.isPending} onClick={() => mut.mutate()}>
            Salvar correção
          </button>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function CreditDialog({ kind, statement, onClose }: { kind: 'inicial' | 'abertura' | 'adicional'; statement: BankStatement; onClose: () => void }) {
  const qc = useQueryClient();
  const c = statement.contract;
  const [requestKey] = useState(() => crypto.randomUUID());
  const [form, setForm] = useState({
    hours: '',
    rate: c.hourlyRate ? c.hourlyRate.replace('.', ',') : '',
    alreadyInvoiced: kind === 'abertura',
    reference: '',
    occurredOn: kind === 'inicial' && c.startDate && c.startDate <= todayIso() ? c.startDate : todayIso(),
  });
  const [localError, setLocalError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => {
      const minutes = kind === 'inicial' ? null : parseHoursInput(form.hours);
      if (kind !== 'inicial' && !minutes) throw new Error('Informe as horas (ex.: 8 ou 1h30).');
      const hourlyRate = kind === 'adicional' ? parseMoneyInput(form.rate) : null;
      if (kind === 'adicional' && !hourlyRate) throw new Error('Valor da hora inválido.');
      return api.post(`/contracts/${c.id}/credits`, { kind, minutes, hourlyRate, alreadyInvoiced: form.alreadyInvoiced, reference: form.reference, occurredOn: form.occurredOn, requestKey });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['statement', c.id] });
      void qc.invalidateQueries({ queryKey: ['hour-banks'] });
      void qc.invalidateQueries({ queryKey: ['billing-items'] });
      onClose();
    },
    onError: (e) => setLocalError(e instanceof Error && !(e as { status?: number }).status ? e.message : null),
  });
  const minutes = parseHoursInput(form.hours);
  const rate = parseMoneyInput(form.rate);
  const preview =
    kind === 'inicial'
      ? `${hm(c.contractedMinutes)} · ${money(c.totalValue)}`
      : kind === 'adicional' && minutes && rate
        ? `${hm(minutes)} × ${money(rate)} = ${money(((minutes / 60) * Number(rate)).toFixed(2))}`
        : null;
  const title = { inicial: 'Registrar contratação do banco', abertura: 'Importar saldo de abertura', adicional: 'Adicionar horas ao banco' }[kind];

  return (
    <Dialog title={title} onClose={onClose}>
      <div className="form">
        {localError && <div className="alert alert-error">{localError}</div>}
        <ErrorAlert error={localError ? null : m.error} />
        {kind === 'abertura' && (
          <div className="alert alert-info small">
            Use para bancos já faturados antes do aplicativo: informe apenas o <strong>saldo remanescente</strong> na data de corte e a referência do faturamento
            original. Não gera nenhuma cobrança.
          </div>
        )}
        {kind === 'inicial' ? (
          <p>
            Crédito: <strong>{preview}</strong> (conforme o contrato).
          </p>
        ) : (
          <Field label={kind === 'abertura' ? 'Saldo remanescente (horas)' : 'Horas adicionais'} required hint="Ex.: 8, 1,5 ou 1h30">
            {(p) => <input {...p} inputMode="decimal" value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} />}
          </Field>
        )}
        {kind === 'adicional' && (
          <Field label="Valor da hora (R$)" required error={fieldError(m.error, 'hourlyRate')}>
            {(p) => <input {...p} inputMode="decimal" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} />}
          </Field>
        )}
        {kind === 'adicional' && preview && <p className="small">Cobrança das horas adicionais: <strong>{preview}</strong></p>}
        {kind !== 'abertura' && (
          <label className="check">
            <input type="checkbox" checked={form.alreadyInvoiced} onChange={(e) => setForm((f) => ({ ...f, alreadyInvoiced: e.target.checked }))} />
            Já foi faturado fora do aplicativo (não gerar item faturável)
          </label>
        )}
        {(form.alreadyInvoiced || kind === 'abertura') && (
          <Field label="Documento / referência do faturamento" required>
            {(p) => <input {...p} value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} placeholder="Ex.: NF 1234" />}
          </Field>
        )}
        {!form.alreadyInvoiced && kind !== 'abertura' && <p className="small muted">Será criado um único item faturável para esta contratação, para conferência antes do envio ao financeiro.</p>}
        <Field label={kind === 'abertura' ? 'Data de corte' : kind === 'inicial' ? 'Data da contratação' : 'Data da compra'} hint="Define a posição no extrato (ordem cronológica).">
          {(p) => <input {...p} type="date" value={form.occurredOn} onChange={(e) => setForm((f) => ({ ...f, occurredOn: e.target.value }))} />}
        </Field>
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending} onClick={() => (setLocalError(null), m.mutate())}>
            Registrar
          </button>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function AdjustmentDialog({ contractId, onClose }: { contractId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [sign, setSign] = useState<1 | -1>(-1);
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/contracts/${contractId}/adjustments`, { minutes: sign * (parseHoursInput(hours) ?? 0), reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['statement', contractId] });
      void qc.invalidateQueries({ queryKey: ['hour-banks'] });
      onClose();
    },
  });
  return (
    <Dialog title="Ajuste identificado" onClose={onClose}>
      <div className="form">
        <p className="small muted">Correção manual do saldo, registrada como movimento próprio com motivo. Não altera execuções nem cobranças.</p>
        <ErrorAlert error={m.error} />
        <div className="segmented" role="group" aria-label="Tipo de ajuste">
          <button type="button" aria-pressed={sign === 1} onClick={() => setSign(1)}>
            Acrescentar
          </button>
          <button type="button" aria-pressed={sign === -1} onClick={() => setSign(-1)}>
            Retirar
          </button>
        </div>
        <Field label="Horas" required>
          {(p) => <input {...p} inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />}
        </Field>
        <Field label="Motivo" required>
          {(p) => <textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <div className="actions">
          <button className="btn btn-primary" disabled={!parseHoursInput(hours) || reason.trim().length < 5 || m.isPending} onClick={() => m.mutate()}>
            Registrar ajuste
          </button>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function StatementExport({ contractId }: { contractId: string }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(todayIso());
  return (
    <section className="card">
      <h2>Extrato para o cliente</h2>
      <div className="toolbar">
        <label htmlFor="sx-from" className="small">De</label>
        <input id="sx-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ flex: '0 1 180px' }} />
        <label htmlFor="sx-to" className="small">Até (data de corte)</label>
        <input id="sx-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ flex: '0 1 180px' }} />
      </div>
      <StatementLinks contractId={contractId} from={from} to={to} />
      <RelatedMessages filter={{ contractId }} />
    </section>
  );
}
